import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { PageHeader } from '../../components/PageHeader';
import { eur, tokens as fmt } from '../../lib/format';
import { relative } from '../../lib/time';
import { useStore } from '../../store/StoreProvider';
import {
  agentsAtWork,
  closedSessions,
  runningSessions,
  tokensToday,
  waitingSessions,
} from '../../store/selectors';
import { when } from '../routines/when';
import { Customize } from './Customize';
import { KpiTile } from './KpiTile';
import { useDashboardPrefs } from './prefs';
import type { WidgetId } from './prefs';
import { lastByProject, useDashboard } from './useDashboard';
import { Health } from './widgets/Health';
import { People } from './widgets/People';
import { Projects } from './widgets/Projects';
import { Recent } from './widgets/Recent';
import { Schedule } from './widgets/Schedule';
import { UsageCard } from './widgets/UsageCard';
import { Waiting } from './widgets/Waiting';

// La Dashboard, «Centro di comando» (tela, artboard 28): i numeri del giorno, poi righe di widget
// alte uguali. Ogni numero viene dal registro: senza lavoro la Dashboard lo dice, non lo simula.
// Quali widget vedere si sceglie da «Personalizza».
export function ControlRoom() {
  const { state, openModal } = useStore();
  const nav = useNavigate();
  const data = useDashboard(state.sessions);
  const { prefs, update } = useDashboardPrefs();
  const show = (id: WidgetId) => !prefs.hidden.includes(id);

  const waiting = waitingSessions(state);
  const agents = agentsAtWork(state);
  const running = runningSessions(state);
  const closed = closedSessions(state).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const today = new Date().toDateString();
  const closedToday = closed.filter((s) => new Date(s.updatedAt).toDateString() === today);
  const done = closedToday.filter((s) => s.state === 'completed').length;
  const usage = data.usage;
  const tokensNow = usage ? (usage.tokens[usage.tokens.length - 1] ?? 0) : tokensToday(state);
  const eurNow = usage ? (usage.costEur[usage.costEur.length - 1] ?? 0) : 0;
  const routinesOn = (data.routines ?? []).filter((r) => r.enabled);
  const next = routinesOn
    .map((r) => r.nextRun)
    .filter((x): x is string => !!x)
    .sort()[0];
  const busyProjects = new Set(running.map((s) => s.project).filter(Boolean)).size;

  const newRequest = () => openModal({ kind: 'new-request', title: 'Nuova richiesta' });
  const action =
    state.role === 'viewer' ? null : state.ready ? (
      <Button variant="primary" icon="plus" onClick={newRequest}>
        Nuova richiesta
      </Button>
    ) : (
      <Button variant="primary" icon="server" onClick={() => nav('/providers')}>
        Aggiungi un provider
      </Button>
    );

  // una riga di due widget: se uno è nascosto l'altro prende tutta la larghezza
  const pair = (a: WidgetId, b: WidgetId, left: ReactNode, right: ReactNode, even = false) =>
    show(a) || show(b) ? (
      <div
        className="dash-line"
        data-single={!(show(a) && show(b)) || undefined}
        data-even={even || undefined}
      >
        {show(a) && left}
        {show(b) && right}
      </div>
    ) : null;

  return (
    <div
      className="control-room page"
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <PageHeader
        title={`Ciao, ${capital(state.display || state.user || 'pop')}!`}
        subtitle={greeting(agents.length, waiting.length)}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Customize hidden={prefs.hidden} onChange={(hidden) => update({ hidden })} />
            {action}
          </div>
        }
      />

      <div className="dash-kpis">
        <KpiTile
          label="In lavorazione"
          pop="developer-01"
          value={running.length}
          note={
            busyProjects
              ? `su ${busyProjects} ${busyProjects === 1 ? 'progetto' : 'progetti'}`
              : 'nessun progetto al lavoro'
          }
        />
        <KpiTile
          label="Aspettano te"
          pop="project-manager-01"
          value={waiting.length}
          dot={waiting.length > 0}
          note={waiting.length ? `da ${relative(waiting[0].updatedAt)}` : 'nessuna domanda aperta'}
        />
        <KpiTile
          label="Pops al lavoro"
          pop="team-lead-01"
          value={agents.length}
          note={data.profiles ? `di ${data.profiles.length} nel team` : undefined}
        />
        <KpiTile
          label="Concluse oggi"
          pop="qa-01"
          value={done}
          note={`${closedToday.length - done} fallite o annullate`}
        />
        <KpiTile
          label="Token di oggi"
          pop="analyst-01"
          value={fmt(tokensNow)}
          note={eurNow ? `${eur(eurNow)} di spesa` : 'dalla traccia'}
        />
        <KpiTile
          label="Attività accese"
          pop="scheduler-01"
          value={data.routines ? routinesOn.length : '—'}
          note={
            next
              ? `la prossima ${when(next)}`
              : routinesOn.length
                ? 'partono a mano o con un input'
                : 'nessuna accesa'
          }
        />
      </div>

      {pair(
        'waiting',
        'schedule',
        <Waiting sessions={waiting} />,
        <Schedule routines={data.routines} />,
      )}
      {show('projects') && (
        <Projects
          projects={data.projects}
          last={lastByProject(state.sessions)}
          tasks={data.tasks}
          usage={usage}
          metric={prefs.metric}
          period={prefs.period}
          onPrefs={update}
        />
      )}
      {pair(
        'people',
        'usage',
        <People
          agents={agents}
          sessions={state.sessions}
          tasks={data.tasks}
          profiles={data.profiles}
        />,
        <UsageCard usage={usage} />,
      )}
      {pair(
        'health',
        'recent',
        <Health sse={state.sse} providers={data.providers} git={data.git} updates={data.updates} />,
        <Recent sessions={closed} />,
        true,
      )}
    </div>
  );
}

/** la frase sotto il saluto: chi lavora e chi aspetta, detta come a una persona */
function greeting(working: number, waiting: number): string {
  const pops =
    working === 0
      ? 'Nessun pop è al lavoro'
      : working === 1
        ? 'Un pop è al lavoro'
        : `${working} pop sono al lavoro`;
  const asks =
    waiting === 0
      ? 'nessuno ti aspetta'
      : waiting === 1
        ? 'uno aspetta una tua risposta'
        : `${waiting} aspettano una tua risposta`;
  return `${pops}, ${asks}.`;
}

const capital = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);
